import { APP_URL } from './config'

/**
 * Article bodies are authored for the in-app help center, so they contain
 * root-relative links: `/help/articles/x` for other articles and
 * `/admin/settings`-style paths into the product. On the public site we map
 * help links onto this site's routes and send product links through the web
 * app's `/open/*` resolver, which prefixes the reader's organization.
 */

const HELP_PREFIX = /^\/help(?=\/|\?|#|$)/

export function isExternalHref(href: string) {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(href)
}

export function toSiteRoute(helpPath: string): string | null {
  if (!HELP_PREFIX.test(helpPath)) return null
  const rest = helpPath.replace(HELP_PREFIX, '')
  return rest ? rest : '/'
}

export function toAppUrl(path: string) {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${APP_URL}/open${normalized}`
}

export interface ResolvedLink {
  href: string
  kind: 'site' | 'app' | 'external' | 'anchor'
}

export function resolveArticleHref(href: string): ResolvedLink {
  if (!href) return { href, kind: 'anchor' }
  if (href.startsWith('#')) return { href, kind: 'anchor' }
  if (isExternalHref(href)) return { href, kind: 'external' }

  const normalized = href.startsWith('/') ? href : `/${href}`
  const siteRoute = toSiteRoute(normalized)
  if (siteRoute) return { href: siteRoute, kind: 'site' }
  return { href: toAppUrl(normalized), kind: 'app' }
}

/**
 * Rewrites anchor hrefs inside sanitized article HTML. Pure string transform so
 * the prerenderer and the browser produce byte-identical markup.
 */
export function rewriteArticleHtml(html: string) {
  return html.replace(
    /<a\b([^>]*?)\shref=(["'])([^"']*)\2([^>]*)>/gi,
    (_match, before: string, quote: string, href: string, after: string) => {
      const resolved = resolveArticleHref(href)
      const attrs = `${before} ${after}`
        .replace(/\s(?:target|rel)=(["'])[^"']*\1/gi, '')
        .trim()
      const extra =
        resolved.kind === 'app' || resolved.kind === 'external'
          ? ' target="_blank" rel="noopener noreferrer"'
          : ''
      const dataKind = ` data-link="${resolved.kind}"`
      return `<a${attrs ? ` ${attrs}` : ''} href=${quote}${resolved.href}${quote}${extra}${dataKind}>`
    },
  )
}
